// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Looking a sentence up (01 §6, 08 §3).
//
// **A function and a table, and nothing else.** No library, no loader, no lazy chunk, no
// interpolation engine that can run code. Two languages and about a hundred and forty strings do
// not need machinery; they need a `Record` and a `replace`, and every line of machinery here would
// be a line between a player and a sentence they were waiting for.
//
// **Substitution is by name and never by position.** `{amount}` and `{percent}` are named because
// French puts them in a different place than English does — "save 8%" against "économisez 8 %" — so
// a positional format string would be a format string that is only correct in one language. A
// placeholder with no matching value is left standing rather than blanked, because a visible
// `{amount}` is a bug somebody reports and an empty space is a bug nobody notices.
//
// **The bundle is chosen once and the lookup cannot miss.** `MessageKey` is `keyof typeof EN`, and
// `FR` is typed as a complete record of it, so there is no runtime path where a key resolves to
// nothing. What the fallback below exists for is the *other* direction: a capability id or an error
// code arriving from the server that this build has never heard of.

import { EN, type MessageKey } from './en'
import { FR } from './fr'
import type { Language } from './language'

export { EN, MESSAGE_KEYS, type MessageKey } from './en'
export { FR } from './fr'
export * from './language'

const BUNDLES: Readonly<Record<Language, Readonly<Record<MessageKey, string>>>> = {
  en: EN,
  fr: FR,
}

export type Values = Readonly<Record<string, string | number>>

/**
 * Put the values into a template.
 *
 * Exported because the arcade badges and the paywall both build sentences from a template that
 * came from somewhere other than a key — a server message with a placeholder in it, most often.
 */
export function format(template: string, values: Values = {}): string {
  return template.replace(/\{(\w+)\}/gu, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole,
  )
}

/** The sentence for this key, in this language, with these values in it. */
export function translate(key: MessageKey, language: Language, values?: Values): string {
  return format(BUNDLES[language][key], values)
}

/**
 * A translator bound to one language, which is what a component wants.
 *
 * Handing components a `t` rather than making each of them pass the language to every call is not
 * only tidier — it is what stops a component translating half its strings into one language and
 * half into whatever was in scope.
 */
export type Translator = (key: MessageKey, values?: Values) => string

export function translatorFor(language: Language): Translator {
  return (key, values) => translate(key, language, values)
}

/**
 * A sentence for a key that **might not exist in this build**, with a fallback.
 *
 * For the two vocabularies that arrive over the wire rather than being compiled in: capability ids
 * and error codes. The contract calls both of them closed and versioned, and they are — but a
 * client is deployed independently of a server, so *this* build may legitimately be older than the
 * code it is being handed. A page that renders `error.some_new_code` at a player is a page that has
 * made the server's forward compatibility into the reader's problem.
 *
 * The fallback is what the server sent, which is English prose and better than a key.
 */
export function translateOr(
  key: string,
  language: Language,
  fallback: string,
  values?: Values,
): string {
  const bundle = BUNDLES[language] as Readonly<Record<string, string | undefined>>
  const found = bundle[key]
  return found === undefined ? format(fallback, values) : format(found, values)
}
